import { useState } from "react";
import { Collapse, Empty, Pagination, Spin, Tooltip } from "antd";
import { TbChevronUp, TbChevronDown, TbChevronRight, TbMail, TbMessageCircle, TbLoader2 } from "react-icons/tb";
import { useSearchParams } from "react-router-dom";
import { ApiService } from "api/api.service";
import type { T_EmailThread, T_Message } from "dtos/communication-channel.dto";
// add to imports at the top
import { TbLayoutSidebarRightCollapse, TbLayoutSidebarRightExpand } from "react-icons/tb";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

type T_ThreadPanelProps = {
  focusedMessageId: string | null;
  onMessageFocus: (messageId: string) => void;
  isOpen: boolean;
  onClose: () => void;
};

const PAGE_SIZE = 20;
function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / 86_400_000);
  if (diffDays === 0) return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: "short" });
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

function MessageItem({ message, isActive, onClick }: { message: T_Message; isActive: boolean; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      className="flex items-start gap-2 cursor-pointer"
      style={{
        padding: "7px 12px 7px 10px",
        borderLeft: `2px solid ${isActive ? "#f59e0b" : "transparent"}`,
        background: isActive ? "#fffbeb" : undefined,
        transition: "background 0.1s",
      }}
      onMouseEnter={(e) => {
        if (!isActive) (e.currentTarget as HTMLDivElement).style.background = "#f8fafc";
      }}
      onMouseLeave={(e) => {
        if (!isActive) (e.currentTarget as HTMLDivElement).style.background = "transparent";
      }}
    >
      <span
        className="rounded-full shrink-0"
        style={{ width: 6, height: 6, marginTop: 2, color: message.outgoing ? "#818cf8" : "#22c55e" }}
      >
        {!message?.outgoing ? <FaArrowRight /> : <FaArrowLeft />}
      </span>
      <span
        className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
        style={{ fontSize: 12, color: "#374151" }}
      >
        {message.snippet || message.text}
      </span>
      <span className="shrink-0" style={{ fontSize: 10.5, color: "#94a3b8" }}>
        {formatTime(message.deliveredAt)}
      </span>
    </div>
  );
}

function ThreadMessages({
  thread_id,
  focusedMessageId,
  onMessageFocus,
}: {
  thread_id: string;
  focusedMessageId: string | null;
  onMessageFocus: (id: string) => void;
}) {
  const { data, isLoading } = ApiService.communicationChannel.useGetMessagesByThreadId({ thread_id });
  const messages = data?.result?.data ?? [];

  if (isLoading)
    return (
      <div className="flex items-center justify-center py-4">
        <Spin size="small" />
      </div>
    );
  if (!messages.length)
    return <div style={{ padding: "10px 14px", fontSize: 12, color: "#94a3b8" }}>No messages in this thread</div>;

  return (
    <div>
      {messages.map((msg) => (
        <MessageItem
          key={msg._id}
          message={msg}
          isActive={focusedMessageId === msg._id}
          onClick={() => onMessageFocus(msg._id)}
        />
      ))}
    </div>
  );
}

//  ThreadHeader

function ThreadHeader({ thread }: { thread: T_EmailThread }) {
  const isEmail = thread.type === "email";
  // overflow: "hidden",
  return (
    <div className="flex items-center justify-between w-full gap-2 overflow-hidden">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {isEmail ? (
          <TbMail size={13} style={{ color: "#94a3b8", flexShrink: 0 }} />
        ) : (
          <TbMessageCircle size={13} style={{ color: "#94a3b8", flexShrink: 0 }} />
        )}
        <Tooltip title={thread.subject ?? "(no subject)"}>
          <span
            className="overflow-hidden max-w-[280px] text-ellipsis whitespace-nowrap"
            style={{ fontSize: 12, color: "#1e293b" }}
          >
            {thread.subject ?? "(no subject)"}
          </span>
        </Tooltip>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span style={{ fontSize: 10.5, color: "#94a3b8" }}>{formatTime(thread.lastMessageAt)}</span>
        <span
          style={{
            background: "#1e293b",
            color: "#fff",
            borderRadius: 10,
            padding: "1px 7px",
            fontSize: 10.5,
            fontWeight: 600,
            lineHeight: "18px",
            minWidth: 20,
            textAlign: "center",
          }}
        >
          {thread.messageCount}
        </span>
      </div>
    </div>
  );
}

//  ThreadPanel

export default function ThreadPanel({ focusedMessageId, onMessageFocus, isOpen, onClose }: T_ThreadPanelProps) {
  const [searchParams] = useSearchParams();
  const person_id = searchParams.get("person_id");
  const [page, setPage] = useState(1);
  const { data, isLoading } = ApiService.communicationChannel.useGetThreadsByPerson({
    person_id,
    page,
    limt: PAGE_SIZE,
  });
  const total = data?.result?.totalObjects ?? 0;
  const threads = data?.result?.data ?? [];

  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);
  // const [isOpen, setIsOpen] = useState(true);

  const navigate = (delta: 1 | -1) => {
    const next = Math.min(Math.max(focusedIndex + delta, 0), threads.length - 1);
    setFocusedIndex(next);
    const target = threads[next];
    if (target) setActiveKeys([target._id]);
  };

  const collapseItems = threads.map((thread) => ({
    key: thread._id,
    label: <ThreadHeader thread={thread} />,
    children: (
      <ThreadMessages thread_id={thread._id} focusedMessageId={focusedMessageId} onMessageFocus={onMessageFocus} />
    ),
  }));

  return (
    <>
      <style>{`
        .refs-panel .ant-collapse-item { border-bottom: 1px solid #f1f5f9 !important; border-radius: 0 !important; }
        .refs-panel .ant-collapse-header { padding: 7px 12px !important; background: #f8fafc !important; align-items: center !important; }
        .refs-panel .ant-collapse-header:hover { background: #f1f5f9 !important; }
        .refs-panel .ant-collapse-content { border-top: 1px solid #f1f5f9 !important; }
        .refs-panel .ant-collapse-content-box { padding: 4px 0 !important; }
        .refs-panel .ant-collapse-expand-icon { padding-inline-end: 6px !important; color: #94a3b8 !important; }
      `}</style>

      <div
        className="refs-panel overflow-hidden"
        // style={{ maxWidth: 400, width: "100%", border: "1px solid #e2e8f0", borderRadius: 8, background: "#ffffff" }}
        style={{
          width: isOpen ? 400 : 0,
          minWidth: isOpen ? 400 : 0,
          transition: "width 0.2s ease, min-width 0.2s ease",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          background: "#ffffff",
          overflow: "hidden", // clips body content during animation
        }}
      >
        <div
          className="flex items-center justify-between"
          style={{ padding: "8px 12px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc" }}
        >
          <button
            onClick={() => onClose()}
            className="flex items-center justify-center rounded"
            style={{
              width: 24,
              height: 24,
              border: "none",
              background: "none",
              color: "#475569",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {isOpen ? <TbLayoutSidebarRightCollapse size={16} /> : <TbLayoutSidebarRightExpand size={16} />}
          </button>
          {isOpen && (
            <>
              {" "}
              <span style={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>
                {isLoading ? "Loading…" : `${total} Thread${total !== 1 ? "s" : ""}`}
              </span>
              {
                <div className={`flex items-center gap-0.5 ${threads.length > 0 ? "block" : "invisible"}`}>
                  <button
                    onClick={() => navigate(-1)}
                    disabled={focusedIndex === 0 || !threads.length}
                    className="flex items-center justify-center rounded cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    style={{ width: 24, height: 24, border: "none", background: "none", color: "#475569" }}
                  >
                    <TbChevronUp size={14} />
                  </button>
                  <button
                    onClick={() => navigate(1)}
                    disabled={focusedIndex >= threads.length - 1}
                    className="flex items-center justify-center rounded cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    style={{ width: 24, height: 24, border: "none", background: "none", color: "#475569" }}
                  >
                    <TbChevronDown size={14} />
                  </button>
                </div>
              }
            </>
          )}
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <TbLoader2 className="animate-spin" size={24} />
          </div>
        )}
        {!isLoading && threads.length === 0 && (
          <div style={{ padding: "24px 14px", fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={" No threads found"} />
          </div>
        )}
        {!isLoading && threads.length > 0 && (
          <Collapse
            className="refs-panel !h-[85vh] overflow-auto"
            activeKey={activeKeys}
            onChange={(keys) => setActiveKeys(typeof keys === "string" ? [keys] : (keys as string[]))}
            expandIcon={({ isActive }) => (
              <TbChevronRight
                size={11}
                style={{
                  color: "#94a3b8",
                  transition: "transform 0.2s",
                  transform: isActive ? "rotate(90deg)" : "rotate(0deg)",
                }}
              />
            )}
            style={{ background: "transparent", border: "none", borderRadius: 0 }}
            items={collapseItems}
          />
        )}
        {(data?.result.totalPages ?? 0) > 1 && (
          <div className="flex items-center justify-center mt-2">
            <Pagination
              current={page}
              total={total}
              pageSize={PAGE_SIZE}
              onChange={(p) => setPage(p)}
              size="small"
              showSizeChanger={false}
            />
          </div>
        )}
      </div>
    </>
  );
}
